import {
  Directive,
  ElementRef,
  Input,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { Platform } from '@ionic/angular';
import { IonInput } from '@ionic/angular';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[autoFocusWithCondition]'
})
export class AutofocusWithConditionDirective
  implements AfterViewInit, OnDestroy, OnChanges
{
  @Input() autoFocusWithCondition: boolean | '' = true;
  @Input() autofocusDelay = 300;
  @Input() androidKeyboardDelay = 100;

  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private androidTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private hasInitialized = false;
  private isDestroyed = false;

  constructor(
    private readonly elementRef: ElementRef,
    private readonly platform: Platform
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['autoFocusWithCondition'] && this.hasInitialized) {
      this.attemptFocus();
    }
  }

  ngAfterViewInit(): void {
    this.hasInitialized = true;
    this.attemptFocus();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.clearTimeouts();
  }

  private attemptFocus(): void {
    this.clearTimeouts();

    const shouldFocus =
      this.autoFocusWithCondition === '' ||
      this.autoFocusWithCondition === true;

    if (!shouldFocus || this.isDestroyed) {
      return;
    }

    this.timeoutId = setTimeout(async () => {
      await this.focusElement();
      this.timeoutId = null;
    }, this.autofocusDelay);
  }

  private async focusElement(): Promise<void> {
    if (this.isDestroyed || !this.hasInitialized) {
      return;
    }

    const element = this.elementRef.nativeElement;
    if (!element) {
      this.log('Element not found');
      return;
    }

    if (element.tagName === 'ION-INPUT') {
      await this.focusIonInput(element as IonInput);
    } else {
      this.focusNativeElement(element as HTMLElement);
    }
  }

  private async focusIonInput(element: IonInput): Promise<void> {
    try {
      await element.setFocus();

      if (this.isDestroyed) {
        return;
      }

      if (this.shouldApplyAndroidDelay()) {
        const input = await element.getInputElement();

        if (this.isDestroyed) {
          return;
        }

        if (input) {
          this.androidTimeoutId = setTimeout(() => {
            if (!this.isDestroyed) {
              input.focus();
            }
            this.androidTimeoutId = null;
          }, this.androidKeyboardDelay);
        }
      }
    } catch (error) {
      this.log('Error focusing IonInput element', error);
    }
  }

  private focusNativeElement(element: HTMLElement): void {
    element.focus();
  }

  private shouldApplyAndroidDelay(): boolean {
    return this.platform.is('android') && this.platform.is('capacitor');
  }

  private clearTimeouts(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.androidTimeoutId) {
      clearTimeout(this.androidTimeoutId);
      this.androidTimeoutId = null;
    }
  }

  private log(message: string, error?: unknown): void {
    console.log(`[AutofocusWithConditionDirective] ${message}`, error || '');
  }
}
